import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subscription } from 'rxjs'

import localForageHelper from '@helpers/localforage.helper'
import UserHelper from '@helpers/user.helper'
import User, { ProfileAdditionalInfo } from '@interfaces/user.interface'
import onEnvironmentChange from '@observables/environment.observable'
import onLangChange from '@observables/on-lang-change.observable'
import onUserChange from '@observables/user.observable'
import DakenService from '@services/daken/daken.service'
import LogService from '@services/log/log.service'

export const useLanguage = (lang: 'en' | 'pt' | 'es' = 'en') => {
  const userHelper = useMemo(() => new UserHelper(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const onUserChangeSubscription = useRef<Subscription>(undefined)
  const onEnvironmentChangeSubscription = useRef<Subscription>(undefined)
  const [allowedLanguages, setAllowedLanguages] = useState<string[]>([])
  logService.init('Language Selector')
  const options = [
    {
      label: 'PT-BR',
      value: 'pt',
    },
    {
      label: 'EN-US',
      value: 'en',
    },
    {
      label: 'ES-ES',
      value: 'es',
    },
  ]
  const [value, setValue] = useState<string>('en')
  const [user, setUser] = useState<User>(null)
  const { i18n } = useTranslation()

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user) => {
      setUser(user)
      const storageLang = await localForageHelper.getItem('language')
      let currentLang: any = user?.profileadditionalinfo?.language || storageLang || lang || i18n.language || 'en'
      currentLang = ['pt-br', 'pt'].includes(currentLang) ? 'pt' : currentLang
      document.documentElement.setAttribute('lang', currentLang)
      changeLang({ target: { value: currentLang } })
    })
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((env) => {
      if (env?.langs?.length) {
        setAllowedLanguages(env.langs)
      }
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
      onEnvironmentChangeSubscription.current?.unsubscribe()
    }
  }, [])

  const changeLang = useCallback(
    (event: any) => {
      const { value } = event?.target
      document.documentElement.setAttribute('lang', value)
      if (value !== i18n.language) {
        setValue(value)
        onLangChange.next(value)
        if (user && user?.profileadditionalinfo?.language !== value) {
          updateAdditionalInfos(value)
          userHelper.setUser({
            ...user,
            profileadditionalinfo: {
              ...user.profileadditionalinfo,
              language: value,
            },
          })
        }
        localForageHelper.setItem('language', value)
      }
    },
    [user]
  )

  const parseFlagByLang = (lang: string) => {
    switch (lang) {
      case 'pt':
        return 'br'
      case 'es':
        return 'es'
      case 'en':
        return 'us'
      default:
        return 'us'
    }
  }

  const updateAdditionalInfos = async (language: string) => {
    try {
      const payload = new ProfileAdditionalInfo({ ...user?.profileadditionalinfo, language })
      return await dakenService.updateAdditionalInfo(payload.toUpdateDakenPayload())
    } catch (error) {
      logService.error({
        message: 'Error on update laguage',
        error,
      })
      return language
    }
  }

  useEffect(() => {
    if (value !== i18n.language) {
      const newLang = allowedLanguages.includes(i18n.language) ? i18n.language : 'en'
      setValue(newLang)
    }
  }, [value, i18n.language, allowedLanguages])

  return {
    options,
    parseFlagByLang,
    changeLang,
    currentLanguage: value,
  }
}
